// =============================================================================
// Optim TURPE V2 — les règles de SAISIE du formulaire « Ajuster l'étude »
// (revue de Mathieu du 08/10/2026, DEV-2311, lot F45).
//
// Ce que le client doit vivre :
// - R10 : l'unité de la puissance de raccordement est celle du segment (C5 et
//   C4 : kVA ; C2 et C3 : kW). Elle ne se confirme plus : elle s'écrit à côté
//   du champ, sans sélecteur ni phrase « unité présumée » ;
// - R8 : quand il saisit ses puissances par plage, il lit tout de suite si le
//   dénivelé n'est pas admissible, avec des mots de client, et l'étude ne part
//   pas tant qu'il ne l'est pas. Les règles sont celles du moteur (courtage,
//   origin/main 55a6599) : ordre non décroissant PTE ≤ HPH ≤ HCH ≤ HPB ≤ HCB
//   (`find_best_fta_denivele_C4.py` l. 293, `echec_servi.py`
//   `MESSAGE_DENIVELE_NON_MONOTONE`) ; en C4, au moins une puissance au-dessus
//   de 36 kVA (`bornes_recommandation.py` l. 435, décision A6) ;
// - Q0 : avant le premier lancement, le formulaire part d'un compteur dont on
//   ne connaît que le segment ; les plages à saisir sont celles du segment ;
// - effacement (prod v491) : une puissance saisie puis effacée revient à « non
//   saisie », comme jamais touchée. Le champ numérique vidé rend `null` (et,
//   selon le composant, `NaN` ou une chaîne vide) : `nombreSaisi` est le seul
//   endroit qui dit ce qu'est une valeur saisie.
// =============================================================================

import { formateurFr } from "@modules/courtage/utils/optim-turpe-v2.nombre"
import { customRef, type Ref } from "vue"
import { ftaAClasseDePointe } from "@modules/courtage/utils/optim-turpe-v2.helpers"
import { LIBELLE_CLASSE_HORAIRE } from "@modules/courtage/utils/optim-turpe-v2.libelles"
import type {
    OptimTurpeV2PlageKey,
    OptimTurpeV2Segment,
} from "@modules/courtage/types/optim-turpe-v2"

/** L'ordre du dénivelé, de la plage la plus basse à la plus haute. */
export const ORDRE_DU_DENIVELE: OptimTurpeV2PlageKey[] = [
    "PTE",
    "HPH",
    "HCH",
    "HPB",
    "HCB",
]

/** Le seuil d'un C4 : au moins une puissance au-dessus de 36 kVA (décision A6). */
export const SEUIL_C4_KVA = 36

/**
 * Le segment normalisé de l'étude (`P1` → `C2`, `P3` → `C4`, `P4` → `C5`,
 * même table que le moteur), ou `null` s'il n'est pas l'un des trois.
 */
export function segmentNormalise(
    segment: string | null | undefined
): OptimTurpeV2Segment | null {
    const s = String(segment ?? "")
        .trim()
        .toUpperCase()
    if (s === "C2" || s === "P1") return "C2"
    if (s === "C4" || s === "P3") return "C4"
    if (s === "C5" || s === "P4") return "C5"
    return null
}

/**
 * L'unité de la puissance de raccordement : celle du segment (R10, décision du
 * 08/10/2026). C5 et C4 en kVA, C2 et C3 en kW ; `null` quand le segment n'est
 * pas connu (l'appelant retombe alors sur l'unité des puissances de l'étude).
 */
export function uniteDuRaccordement(
    segment: string | null | undefined
): "kVA" | "kW" | null {
    const s = String(segment ?? "")
        .trim()
        .toUpperCase()
    if (s === "C5" || s === "C4" || s === "P4" || s === "P3") return "kVA"
    if (s === "C2" || s === "C3" || s === "P1") return "kW"
    return null
}

/**
 * Les plages à saisir pour un segment, quand aucune étude ne les dit encore :
 * les quatre plages saisonnières en C4 et en C2, plus la pointe en C2 si la
 * formule en porte une (une formule inconnue ne ferme rien). Aucune en C5.
 */
export function plagesDuSegment(
    segment: string | null | undefined,
    fta?: string | null
): OptimTurpeV2PlageKey[] {
    const s = segmentNormalise(segment)
    if (s === "C4") return ["HPH", "HCH", "HPB", "HCB"]
    if (s === "C2") {
        return ftaAClasseDePointe(fta) === false
            ? ["HPH", "HCH", "HPB", "HCB"]
            : ORDRE_DU_DENIVELE
    }
    return []
}

/**
 * La valeur d'un champ numérique, ou `null` s'il n'est pas saisi : vide,
 * `undefined`, chaîne (vide ou non), `NaN` ou infini valent tous « non saisi ».
 */
export function nombreSaisi(valeur: unknown): number | null {
    return typeof valeur === "number" && Number.isFinite(valeur) ? valeur : null
}

/** Le dénivelé réduit à ses plages saisies : une plage effacée n'y figure plus. */
export function deniveleSaisi(
    denivele: Partial<Record<OptimTurpeV2PlageKey, unknown>> | null | undefined
): Partial<Record<OptimTurpeV2PlageKey, number>> {
    const saisi: Partial<Record<OptimTurpeV2PlageKey, number>> = {}
    for (const [plage, valeur] of Object.entries(denivele ?? {})) {
        const nombre = nombreSaisi(valeur)
        if (nombre !== null) saisi[plage as OptimTurpeV2PlageKey] = nombre
    }
    return saisi
}

/**
 * Deux dénivelés sont égaux quand chaque plage, prise dans l'ordre du
 * dénivelé, porte la même valeur saisie (ou est vide des deux côtés) : l'ordre
 * d'insertion des clés, qui change quand on efface puis ressaisit une plage,
 * ne compte pas.
 */
export function denivelesEgaux(
    a: Partial<Record<OptimTurpeV2PlageKey, unknown>> | null | undefined,
    b: Partial<Record<OptimTurpeV2PlageKey, unknown>> | null | undefined
): boolean {
    return ORDRE_DU_DENIVELE.every(
        (plage) => nombreSaisi(a?.[plage]) === nombreSaisi(b?.[plage])
    )
}

/**
 * Une référence de champ numérique qui ne garde qu'une valeur saisie ou `null`,
 * quoi que le composant de saisie lui écrive.
 */
export function refNombreSaisi(initiale: unknown): Ref<number | null> {
    let valeur = nombreSaisi(initiale)
    return customRef<number | null>((track, trigger) => ({
        get() {
            track()
            return valeur
        },
        set(nouvelle: unknown) {
            const normalisee = nombreSaisi(nouvelle)
            if (Object.is(normalisee, valeur)) return
            valeur = normalisee
            trigger()
        },
    }))
}

function formatKva(valeur: number, unite: string): string {
    const texte = formateurFr({
        maximumFractionDigits: 2,
    }).format(valeur)
    return `${texte} ${unite}`
}

/**
 * Le dénivelé saisi est-il admissible ? Rend la phrase à dire au client, ou
 * `null` quand il l'est. Seules les plages renseignées comptent : une plage
 * vide n'est pas un 0.
 *
 * Lot 4d (contrat v1.2 §8.2.2) : quand `champ` est donné (« Puissances à
 * chiffrer », « Contrat actuel › Puissances souscrites »), la phrase est celle
 * du refus `validation-denivele-non-admissible` du moteur (courtage
 * `denivele_saisi.py`, `message_denivele_non_admissible`) : le champ et les
 * deux postes nommés, avec leurs valeurs.
 */
export function refusDuDenivele(
    denivele: Partial<Record<OptimTurpeV2PlageKey, number | null>>,
    segment: string | null | undefined,
    unite: string,
    champ?: string
): string | null {
    const saisies = ORDRE_DU_DENIVELE.flatMap((plage) => {
        const valeur = nombreSaisi(denivele[plage])
        return valeur === null ? [] : [{ plage, valeur }]
    })
    if (saisies.length === 0) return null
    for (let i = 1; i < saisies.length; i++) {
        const avant = saisies[i - 1]
        const apres = saisies[i]
        if (apres.valeur < avant.valeur) {
            if (champ) {
                return (
                    `Puissances par poste dans « ${champ} » : ${LIBELLE_CLASSE_HORAIRE[apres.plage]} à ${formatKva(apres.valeur, unite)}, ` +
                    `sous ${LIBELLE_CLASSE_HORAIRE[avant.plage]} à ${formatKva(avant.valeur, unite)}. ` +
                    "Chaque puissance doit être au moins égale à celle du poste précédent."
                )
            }
            const depuis =
                saisies[0].plage === "PTE"
                    ? "des heures de pointe"
                    : "des heures pleines de saison haute"
            return (
                `Les puissances doivent aller en croissant ${depuis} aux heures creuses de saison basse. ` +
                `Ici, la puissance des ${LIBELLE_CLASSE_HORAIRE[apres.plage]} (${formatKva(apres.valeur, unite)}) ` +
                `est inférieure à celle des ${LIBELLE_CLASSE_HORAIRE[avant.plage]} (${formatKva(avant.valeur, unite)}).`
            )
        }
    }
    if (
        segmentNormalise(segment) === "C4" &&
        saisies.every((s) => s.valeur <= SEUIL_C4_KVA)
    ) {
        return `Pour un compteur C4, au moins une des puissances doit dépasser ${SEUIL_C4_KVA} kVA.`
    }
    return null
}

// -----------------------------------------------------------------------------
// Saisies robustes (chasse aux cas limites du 20/10/2026)
// -----------------------------------------------------------------------------

/**
 * La puissance souscrite la plus haute qu'un compteur du segment peut porter :
 * 36 kVA en C5, 250 kVA en C4 (basse tension au-delà de 36 kVA), comme le
 * moteur (courtage `PLAFOND_SOUSCRIPTIBLE_PAR_SEGMENT`, qui ne sert pas
 * l'économie d'une souscrite au-dessus) ; en C2, aucun plafond tarifaire,
 * seulement un garde-fou contre la faute de frappe.
 *
 * Elle ne se pose plus en `:max` sur le champ : le champ numérique RAMENAIT à
 * sa borne en silence (un C2 prérempli à 1 500 kW devenait 999 kW au premier
 * passage dans le champ). Elle se dit par une phrase et bloque le lancement.
 */
export const PUISSANCE_MAX_DU_SEGMENT: Record<OptimTurpeV2Segment, number> = {
    C5: 36,
    C4: 250,
    C2: 99_999,
}

/**
 * Une puissance TAPÉE à 0 sur ce poste est-elle admise ? Décision A6 de
 * Mathieu (DECISIONS-A1-A20) : en C4, « au moins une puissance du dénivelé
 * dépasse 36 kVA, les autres classes sont libres ». Le 0 est donc admis sur
 * tout poste affiché autre que le plus haut du dénivelé ; l'ordre des postes
 * et le seuil de 36 kVA restent jugés par `refusDuDenivele`. Le plus haut
 * poste, le C5 et le C2 gardent le refus du 0 ; un négatif ou une décimale
 * restent refusés partout. Un champ vide n'est jamais lu comme 0.
 */
export function zeroAdmisSurLePoste(
    segment: string | null | undefined,
    plage: OptimTurpeV2PlageKey,
    affiches: ReadonlyArray<OptimTurpeV2PlageKey>
): boolean {
    if (segmentNormalise(segment) !== "C4") return false
    const plusHaut = [...ORDRE_DU_DENIVELE]
        .reverse()
        .find((p) => affiches.includes(p))
    return plusHaut !== undefined && plage !== plusHaut
}

/**
 * Les puissances souscrites saisies sont-elles envoyables ? Rend la phrase à
 * dire sous le champ, ou `null`. Seules les valeurs saisies comptent (un champ
 * vide n'est pas un 0, d'autres refus le traitent).
 *
 * - une puissance qui n'est pas un entier strictement positif (0, négative,
 *   décimale) : une puissance souscrite se souscrit au pas de 1 kVA en BT,
 *   1 kW en HTA (courtage `PAS_DE_SOUSCRIPTION`) ;
 * - une puissance au-dessus de la borne du segment (`PUISSANCE_MAX_DU_SEGMENT`).
 *
 * L'appelant ne passe que les valeurs TAPÉES par le client, sur des postes
 * affichés : une valeur lue chez Enedis et laissée telle quelle ne se juge pas.
 */
export function refusDesPuissances(
    valeurs: ReadonlyArray<unknown>,
    segment: string | null | undefined,
    unite: string
): string | null {
    const saisies = valeurs
        .map(nombreSaisi)
        .filter((v): v is number => v !== null)
    if (saisies.some((v) => !Number.isInteger(v) || v <= 0)) {
        return `Les puissances souscrites se saisissent en ${unite} entiers, supérieurs à zéro.`
    }
    const s = segmentNormalise(segment)
    if (s === null) return null
    const max = PUISSANCE_MAX_DU_SEGMENT[s]
    if (saisies.some((v) => v > max)) {
        return `Sur un compteur ${s}, une puissance souscrite ne dépasse pas ${formatKva(max, unite)}.`
    }
    return null
}

// -----------------------------------------------------------------------------
// Revue témoin du 15/10/2026 : contrôles locaux de la saisie, avant l'envoi
// -----------------------------------------------------------------------------

/**
 * Le segment d'une formule d'acheminement, lu sur son code (D13) :
 * basse tension jusqu'à 36 kVA (C5), au-delà (C4), haute tension (C2). `null`
 * pour un code que la page ne sait pas classer : rien n'est présumé.
 */
export function segmentDeLaFormule(
    fta: string | null | undefined
): OptimTurpeV2Segment | null {
    const code = (fta ?? "").trim().toUpperCase()
    if (code.startsWith("BTINF")) return "C5"
    if (code.startsWith("BTSUP")) return "C4"
    if (code.startsWith("HTA")) return "C2"
    return null
}

/**
 * D13 : le segment choisi contredit-il la formule saisie ? Une formule
 * inconnue, ou aucune formule, ne contredit rien.
 */
export function segmentIncoherentAvecLaFormule(
    segment: string | null | undefined,
    fta: string | null | undefined
): boolean {
    const deLaFormule = segmentDeLaFormule(fta)
    // Revue sceptique de L-D : un segment que la page ne sait pas normaliser
    // (le segment brut du compteur) n'est pas déclaré incohérent ; le moteur
    // reste juge.
    const normalise = segment ? segmentNormalise(segment) : null
    if (normalise === null || deLaFormule === null) return false
    return normalise !== deLaFormule
}

/**
 * D14 : une période saisie avec une seule date n'est pas une période. Vrai
 * quand l'une des deux dates manque et l'autre est posée.
 */
export function uneSeuleDate(
    dateFrom: string | null | undefined,
    dateTo: string | null | undefined
): boolean {
    return Boolean(dateFrom) !== Boolean(dateTo)
}

const HEURE = /^([01]\d|2[0-3]):([0-5]\d)$/

function minutes(heure: string): number | null {
    const m = HEURE.exec(heure.trim())
    return m ? Number(m[1]) * 60 + Number(m[2]) : null
}

/** Les intervalles d'une plage sur la journée, la nuit coupée à minuit. */
function intervalles(debut: number, fin: number): Array<[number, number]> {
    return debut < fin
        ? [[debut, fin]]
        : [
              [debut, 1440],
              [0, fin],
          ]
}

/**
 * D22 : la raison pour laquelle les plages d'heures creuses saisies ne
 * partent pas, ou `null` quand elles sont envoyables. Une heure se saisit
 * « HH:MM » (« 2200 » ne part plus au moteur) ; une plage a deux heures
 * distinctes ; deux plages ne se chevauchent pas.
 */
export function refusDesPlagesHoraires(
    plages: ReadonlyArray<{ from: string; to: string }>
): string | null {
    const lues: Array<[number, number]> = []
    for (const plage of plages) {
        const debut = minutes(plage.from ?? "")
        const fin = minutes(plage.to ?? "")
        if (debut === null || fin === null)
            return "Saisissez chaque heure sous la forme HH:MM, par exemple 22:00."
        if (debut === fin)
            return "Une plage commence et finit à deux heures différentes."
        lues.push([debut, fin])
    }
    for (let i = 0; i < lues.length; i++) {
        for (let j = i + 1; j < lues.length; j++) {
            const a = intervalles(...lues[i])
            const b = intervalles(...lues[j])
            if (a.some(([d1, f1]) => b.some(([d2, f2]) => d1 < f2 && d2 < f1)))
                return "Deux plages se chevauchent : corrigez leurs heures."
        }
    }
    return null
}
